import apiClient, { extractData, handleApiError } from './api.js';

export class InventoryService {
  /**
   * Get all inventory items for a specific date and outlet
   * GET /api/v1/inventory/{outlet_id}?date=YYYY-MM-DD
   * @param {string} outletId
   * @param {string} date - YYYY-MM-DD format
   * @returns {Promise<{items: Array, summary: Object}>}
   */
  static async fetchInventory(outletId, date) {
    try {
      const response = await apiClient.get(`/inventory/${outletId}`, {
        params: { date },
      });
      return extractData(response);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Add a new inventory item for a specific date
   * POST /api/v1/inventory/{outlet_id}
   * @param {string} outletId
   * @param {Object} itemData - { item_name, quantity, unit, amount, date, used? }
   * @returns {Promise<Object>}
   */
  static async addInventoryItem(outletId, itemData) {
    try {
      const response = await apiClient.post(`/inventory/${outletId}`, {
        item_name: itemData.item_name,
        quantity: itemData.quantity,
        unit: itemData.unit,
        amount: itemData.amount,
        date: itemData.date,
        used: itemData.used,
      });
      return extractData(response);
    } catch (error) {
      console.error('Error adding inventory item:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update an existing inventory item
   * PUT /api/v1/inventory/{outlet_id}/{item_id}
   * @param {string} outletId
   * @param {string} itemId
   * @param {Object} updateData - { item_name?, quantity?, unit?, amount?, used? }
   * @returns {Promise<Object>}
   */
  static async updateInventoryItem(outletId, itemId, updateData) {
    try {
      const response = await apiClient.put(`/inventory/${outletId}/${itemId}`, updateData);
      return extractData(response);
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Delete an inventory item
   * DELETE /api/v1/inventory/{outlet_id}/{item_id}
   * @param {string} outletId
   * @param {string} itemId
   * @returns {Promise<boolean>}
   */
  static async deleteInventoryItem(outletId, itemId) {
    try {
      await apiClient.delete(`/inventory/${outletId}/${itemId}`);
      return true;
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Copy inventory from one date to another
   * POST /api/v1/inventory/{outlet_id}/copy-previous
   * @param {string} outletId
   * @param {string} sourceDate - YYYY-MM-DD
   * @param {string} targetDate - YYYY-MM-DD
   * @returns {Promise<Object>}
   */
  static async copyPreviousInventory(outletId, sourceDate, targetDate) {
    try {
      const response = await apiClient.post(`/inventory/${outletId}/copy-previous`, {
        source_date: sourceDate,
        target_date: targetDate,
      });
      return extractData(response);
    } catch (error) {
      console.error('Error copying inventory:', error);
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Helper: fetch today's inventory
   * @param {string} outletId
   * @returns {Promise<{items: Array, summary: Object}>}
   */
  static async fetchTodayInventory(outletId) {
    const today = new Date().toISOString().split('T')[0];
    return this.fetchInventory(outletId, today);
  }
}
